import { format, Locale, parseISO } from 'date-fns';
import { enGB, fr } from 'date-fns/locale';
import React, { FC, memo, useCallback } from 'react';
import useTranslations from '@hooks/useTranslations';
import { DATE_FORMAT, LANGUAGE, LANGUAGES, PRINT_DATEFORMAT_ERRORS } from '../constants/main';
import { useTranslation } from '../../i18n';

export const LOCALE_FR = 'fr';
export const LOCALE_UK = 'en';

export type LocaleString = typeof LOCALE_FR | typeof LOCALE_UK;
type SourceDate = Date | string;

type Locales = {
    [index in LocaleString]: Locale;
};

const locales: Locales = {
    fr: fr,
    en: enGB,
};

interface IFormatDate {
    date?: SourceDate;
    locale?: LocaleString;
    formatString?: string;
}

export function formatDate(date: Date, formatString: string, locale?: Locale) {
    return format(date, formatString, { locale });
}

export function formatDateByLang(date: string | Date): string {
    try {
        switch (LANGUAGE) {
            case LANGUAGES.EN:
                return formatDate(new Date(date), DATE_FORMAT.EN, locales.en);
            case LANGUAGES.FR:
                return formatDate(new Date(date), DATE_FORMAT.FR, locales.fr);
            default:
                return formatDate(new Date(date), DATE_FORMAT.FR, locales.fr);
        }
    } catch (err) {
        console.error('Caught exception when formatting date - formatDateByLang', err);
        return '';
    }
}

export const useDateUtils = () => {
    const { t } = useTranslations();
    const {
        i18n: { language },
    } = useTranslation();

    const formatDateFn = useCallback(
        (date?: SourceDate, locale?: LocaleString, formatString?: string) => {
            if (!date) return null;

            const dateToFormat = typeof date === 'string' ? parseISO(date) : date;
            const defaultFormatString = t('dateFormat.default');

            try {
                return formatDate(
                    dateToFormat,
                    formatString || defaultFormatString,
                    locales[locale || (language as LocaleString)]
                );
            } catch (e) {
                if (process.env.NODE_ENV !== 'production' || PRINT_DATEFORMAT_ERRORS) {
                    console.group('FORMAT DATE ERROR');
                    console.log('Format:', formatString || defaultFormatString);
                    console.log('Date:', date);
                    console.error(e);
                    console.groupEnd();
                }
                return formatDate(dateToFormat, 'dd/MM/yyyy');
            }
        },
        [t]
    );

    const FormattedDate: FC<IFormatDate> = ({ date, locale, formatString }) => {
        if (!date) return null;
        return (
            <time dateTime={typeof date === 'string' ? date : date.toISOString()}>
                {formatDateFn(date, locale, formatString)}
            </time>
        );
    };

    return { formatDate: formatDateFn, FormattedDate: memo(FormattedDate) };
};

export default formatDate;
